import type { VariantProps } from 'class-variance-authority';

import { forwardRef } from 'react';
import * as PopoverPrimitives from '@radix-ui/react-popover';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

export const Popover = PopoverPrimitives.Root;
export const PopoverTrigger = PopoverPrimitives.Trigger;
export const PopoverClose = PopoverPrimitives.Close;
export const PopoverArrow = PopoverPrimitives.Arrow;
export const PopoverAnchor = PopoverPrimitives.Anchor;

const popoverContentVariants = cva(
  'max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] min-w-[250px] rounded-md border border-grey-mid bg-white shadow-md will-change-[transform,opacity] data-[state=open]:data-[side=bottom]:animate-slideUpAndFade data-[state=open]:data-[side=left]:animate-slideRightAndFade data-[state=open]:data-[side=right]:animate-slideLeftAndFade data-[state=open]:data-[side=top]:animate-slideDownAndFade',
);

export interface PopoverContentProps
  extends PopoverPrimitives.PopoverContentProps,
    VariantProps<typeof popoverContentVariants> {
  /**
   * Container the content is portaled into. Defaults to `document.body`. Pass a
   * specific element when the popover lives inside a modal Radix Dialog, whose
   * `react-remove-scroll` lock only allows scrolling within the dialog subtree.
   */
  container?: HTMLElement;
}

export const PopoverContent = forwardRef<HTMLDivElement, PopoverContentProps>((props, ref) => {
  const { hidden, children, className, container, ...restProps } = props;

  if (hidden || !children) {
    return null;
  }

  return (
    <PopoverPrimitives.PopoverPortal container={container}>
      <PopoverPrimitives.Content
        ref={ref}
        align="start"
        sideOffset={5}
        collisionPadding={10}
        className={twMerge(
          popoverContentVariants({
            className,
          }),
        )}
        {...restProps}
      >
        {children}
      </PopoverPrimitives.Content>
    </PopoverPrimitives.PopoverPortal>
  );
});

PopoverContent.displayName = 'PopoverContent';
